import React, { useCallback, useMemo } from 'react';
import cx from 'clsx';

import { ChangeTokenList, UserCardPopup, UserAvatar, UserName } from '@/components';
import { useDeviceTypeIsMobile } from '@/hooks';
import { openNewWindow, shorten } from '@/utils';
import { BotAlert, BotDetail, BotFilter } from '@/views/Bot/types';
import { TransactionAction } from '@/views/History';

import style from './index.module.scss';

type BotEventFilterProps = {
  className?: string;
  data?: BotFilter;
};

export const BotEventFilter = ({ className, data }: BotEventFilterProps) => {
  const isMobile = useDeviceTypeIsMobile();

  return (
    <div className={cx(style.botEventFilter, isMobile && style.isMobile, className)}>
      <div className={style.botEventName}>{data?.label}</div>
      <div className={style.botEventCondition}>{data?.tag_value}</div>
    </div>
  );
};

type BotTrackedUserProps = {
  data?: BotDetail;
  isShortAddr?: boolean;
};

export const BotTrackedUser = ({ data, isShortAddr = true }: BotTrackedUserProps) => {
  const user = data?.target;
  const isMobile = useDeviceTypeIsMobile();

  const openProfile = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      openNewWindow(`/profile/${user?.id}`, '_blank');
    },
    [user?.id],
  );

  const address = useMemo(
    () => (isShortAddr && user?.id ? shorten(user?.id, 6, 4) : user?.id),
    [user?.id, isShortAddr],
  );

  return user ? (
    <div className={cx(style.botTrackedUser, isMobile && style.isMobile)}>
      <UserAvatar
        data={user?.desc}
        size={isMobile ? 40 : 54}
        onClick={openProfile}
        className={style.userAvatar}
        userCardPopup
      />
      <div className={style.botInfo}>
        <div className={style.botTracker}>
          <UserCardPopup address={user?.id || ''}>
            <UserName
              name={user?.web3_id}
              address={user?.id}
              onClick={openProfile}
              userDesc={user?.desc}
            />
          </UserCardPopup>
          <span className={style.address}>({address})</span>
        </div>
        <BotEventFilter className={style.eventTag} data={data?.filter} />
      </div>
    </div>
  ) : null;
};

type BotAlertContentProps = {
  data?: BotAlert;
  isShortAddr?: boolean;
  className?: string;
  showTime?: boolean;
  hidePrice?: boolean;
};

export const BotAlertContent = ({
  data,
  className,
  showTime = true,
  hidePrice,
  isShortAddr,
}: BotAlertContentProps) => {
  return (
    <div className={cx(style.botAlert, className)}>
      {data?.event?.kind === 'profile_tx' ? (
        <>
          <div className={style.botAlertHeader}>
            <BotTrackedUser data={data?.bot} isShortAddr={isShortAddr} />
            {showTime ? <span className={style.eventTime}>{data?._time}</span> : null}
          </div>
          <div className={style.botAlertBody}>
            <TransactionAction
              className={style.eventAction}
              info={data?.event}
              projectsDict={data?.project_dict}
              cexs={data?.cex_dict}
              cate={data?.cate_dict}
              tokensDict={data?.token_dict}
              memos={data?.memo_dict}
              projectNameClassName={style.actionProjectName}
              showChain
              showChainTip
            />
            <ChangeTokenList
              className={style.tokenList}
              sends={data?.event?.sends}
              receives={data?.event?.receives}
              tokenDict={data?.token_dict}
              time={data?.event?.time_at}
              flagOffset={16}
              hidePrice={hidePrice}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};
